import { useGetAllProvincesQuery } from '@exam-center/graphql';
import { Button, Divider, Flex, Form } from '@shared/components';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useExamLocationRequestListSearchParams } from '../../hooks';
import { ProvinceSearchSelection } from '../province-search-selection';

export const ExamLocationRequestListFilterFormSchema = z.object({
  province: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .optional(),
});

export type ExamLocationRequestListFilterFormType = z.infer<
  typeof ExamLocationRequestListFilterFormSchema
>;

export default function ExamLocationRequestListFilters() {
  const [{ province }, { changeProvince }] = useExamLocationRequestListSearchParams();
  const { data } = useGetAllProvincesQuery();

  const provinces = data?.ecGetAllProvinces?.provinces ?? [];

  const formContext = useForm<ExamLocationRequestListFilterFormType>({
    defaultValues: {
      province: province
        ? {
            label: provinces.find((item) => item.id === province)?.nameTh ?? '',
            value: province ?? '',
          }
        : undefined,
    },
  });

  useEffect(() => {
    if (province) {
      const list = data?.ecGetAllProvinces?.provinces;
      if (list) {
        const found = list.find((item) => item.id === province);
        if (found) {
          const current = formContext.getValues('province');
          if (!current || current.label !== found.nameTh) {
            formContext.setValue('province', { label: found.nameTh, value: province });
          }
        }
      }
    }
  }, [province, data, formContext]);

  const handleSubmitFilter = () => {
    const { province } = formContext.getValues();

    changeProvince(province?.value ?? '');
  };

  const handleResetFilter = () => {
    formContext.reset();
    handleSubmitFilter();
  };

  return (
    <FormProvider {...formContext}>
      <Form>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(4),
            p: theme.spacing(4),
            border: `1px solid ${theme.palette.grey[300]}`,
            borderRadius: theme.shape.rounded(2),
          })}
        >
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(4),
              maxWidth: 300,
              width: '100%',
            })}
          >
            <ProvinceSearchSelection value={formContext.watch('province')} />
          </Flex>
          <Divider />
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(2),
              justifyContent: 'flex-end',
            })}
          >
            <Button
              variant='outlined'
              color='secondary'
              sx={{ width: '100px' }}
              onClick={handleResetFilter}
            >
              Reset
            </Button>
            <Button variant='contained' sx={{ width: '100px' }} onClick={handleSubmitFilter}>
              Apply
            </Button>
          </Flex>
        </Flex>
      </Form>
    </FormProvider>
  );
}
